import React from 'react';
import {  FiTrendingUp, FiClock, FiFileText, FiUsers, FiAward, FiShield } from 'react-icons/fi';

const BoardComparison = () => {
  const boardRequirements = [
    {
      board: 'CBSE',
      icon: <FiAward className="w-6 h-6" />,
      description: 'CBSE schools need comprehensive reporting, attendance tracking, and academic performance management. They require structured report cards, exam management, and co-scholastic tracking.',
      keyFeatures: 'Attendance reports, exam management, report cards, co-scholastic tracking, CBSE compliance reports',
      color: 'bg-indigo-50'
    },
    {
      board: 'ICSE',
      icon: <FiAward className="w-6 h-6" />,
      description: 'ICSE schools focus on holistic development with a strong emphasis on academics, co-curricular activities, and project-based learning. They need detailed student profiles and comprehensive assessment tools.',
      keyFeatures: 'Academic tracking, project management, co-curricular records, student profiles, assessment tools',
      color: 'bg-emerald-50'
    },
    {
      board: 'State Board',
      icon: <FiAward className="w-6 h-6" />,
      description: 'State board schools often operate across multiple states with different languages and curricula. They need multi-language support, state-specific compliance, and simplified administration.',
      keyFeatures: 'Multi-language support, state-specific reports, simplified UI, compliance tracking',
      color: 'bg-amber-50'
    },
    {
      board: 'University',
      icon: <FiAward className="w-6 h-6" />,
      description: 'Universities need complex systems for program management, credit systems, faculty management, research tracking, and accreditation. They require flexibility for multiple colleges under one umbrella.',
      keyFeatures: 'Program management, credit system, faculty records, research tracking, accreditation reporting',
      color: 'bg-rose-50'
    }
  ];

  const comparisonFeatures = [
    {
      feature: 'Attendance Tracking',
      cbse: '✅ Daily tracking, parental notifications',
      icse: '✅ Daily tracking, holistic records',
      stateBoard: '✅ Basic attendance',
      university: '✅ Semester-based tracking'
    },
    {
      feature: 'Academic Reporting',
      cbse: '✅ Graded report cards',
      icse: '✅ Comprehensive academic + co-curricular',
      stateBoard: '✅ Basic mark sheets',
      university: '✅ Semester transcripts, CGPA'
    },
    {
      feature: 'Exam Management',
      cbse: '✅ Periodic tests, half-yearly, yearly',
      icse: '✅ Project + exams + internal assessment',
      stateBoard: '✅ Annual exams',
      university: '✅ Semester exams, CGPA system'
    },
    {
      feature: 'Curriculum Flexibility',
      cbse: '✅ Structured curriculum',
      icse: '✅ Flexible, project-based',
      stateBoard: '✅ State-defined curriculum',
      university: '✅ Program-based, multiple courses'
    },
    {
      feature: 'Compliance Reports',
      cbse: '✅ CBSE formats',
      icse: '✅ ICSE formats',
      stateBoard: '✅ State board formats',
      university: '✅ UGC/AICTE formats'
    },
    {
      feature: 'Multi-Language Support',
      cbse: '✅ English + Hindi',
      icse: '✅ English + regional',
      stateBoard: '✅ Multiple regional languages',
      university: '✅ English + local languages'
    },
    {
      feature: 'Co-Scholastic Tracking',
      cbse: '✅ Required',
      icse: '✅ Essential',
      stateBoard: '✅ Optional',
      university: '✅ Not typically tracked'
    },
    {
      feature: 'Parent Communication',
      cbse: '✅ Important',
      icse: '✅ Important',
      stateBoard: '✅ Moderate',
      university: '✅ Minimal'
    }
  ];

  const commonNeeds = [
    {
      title: 'Student Data Management',
      description: 'All boards require accurate student data management—admissions, enrollment, personal records, and historical data.',
      icon: <FiUsers className="w-5 h-5" />
    },
    {
      title: 'Teacher/Faculty Records',
      description: 'Every institution needs to track teacher qualifications, attendance, and performance records.',
      icon: <FiUsers className="w-5 h-5" />
    },
    {
      title: 'Attendance Tracking',
      description: 'Attendance is essential across all boards, though the frequency and format may differ.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Report Generation',
      description: 'Every board requires reports—whether for students, parents, or regulatory bodies.',
      icon: <FiFileText className="w-5 h-5" />
    },
    {
      title: 'Security and Privacy',
      description: 'All institutions must protect student and staff data with secure systems and access controls.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Scalability',
      description: 'Institutions need systems that can grow with them—whether adding students or new campuses.',
      icon: <FiTrendingUp className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        India's education system is wonderfully diverse. Schools follow CBSE, ICSE, or state boards. Colleges and universities have their own unique requirements. Each board and institution type has different needs, different reporting formats, and different compliance requirements.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        The question many educational leaders ask is: <strong className="text-slate-900">"Do I need a different ERP for each board or institution type?"</strong> The answer is no—but you need an ERP that can adapt to different requirements.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's a comparison of what matters most for CBSE, ICSE, State Board, and Universities—and how the right ERP handles them all.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-8">
        <div className="bg-white rounded-xl p-4 text-center border">
          <div className="text-lg font-extrabold text-indigo-600">1.5M+</div>
          <div className="text-xs text-slate-500">Schools across India</div>
        </div>
        <div className="bg-white rounded-xl p-4 text-center border">
          <div className="text-lg font-extrabold text-emerald-600">50,000+</div>
          <div className="text-xs text-slate-500">Colleges & Universities</div>
        </div>
        <div className="bg-white rounded-xl p-4 text-center border">
          <div className="text-lg font-extrabold text-amber-600">40+</div>
          <div className="text-xs text-slate-500">State Boards</div>
        </div>
        <div className="bg-white rounded-xl p-4 text-center border">
          <div className="text-lg font-extrabold text-rose-600">1</div>
          <div className="text-xs text-slate-500">Platform for all—Truyon</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">What Each Board and Institution Type Needs</h2>

      {/* Board Requirements */}
      {boardRequirements.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.board}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-3">{item.description}</p>
              <div className="bg-white/80 rounded-xl p-3 border border-indigo-200">
                <p className="text-sm font-semibold text-slate-700">🔑 Key Features: <span className="font-normal text-slate-600">{item.keyFeatures}</span></p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Comparison Table */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 overflow-x-auto">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Feature Comparison: What Matters Most</h3>
        <div className="w-full text-xs sm:text-sm">
          <div className="grid grid-cols-5 gap-1">
            <div className="font-bold text-slate-900 p-2 border-b border-slate-200">Feature</div>
            <div className="font-bold text-indigo-700 p-2 border-b border-slate-200 text-center">CBSE</div>
            <div className="font-bold text-emerald-700 p-2 border-b border-slate-200 text-center">ICSE</div>
            <div className="font-bold text-amber-700 p-2 border-b border-slate-200 text-center">State Board</div>
            <div className="font-bold text-rose-700 p-2 border-b border-slate-200 text-center">University</div>
            
            {comparisonFeatures.map((item, i) => (
              <React.Fragment key={i}>
                <div className="p-2 border-b border-slate-100 font-medium">{item.feature}</div>
                <div className="p-2 border-b border-slate-100 text-center text-indigo-700">{item.cbse}</div>
                <div className="p-2 border-b border-slate-100 text-center text-emerald-700">{item.icse}</div>
                <div className="p-2 border-b border-slate-100 text-center text-amber-700">{item.stateBoard}</div>
                <div className="p-2 border-b border-slate-100 text-center text-rose-700">{item.university}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Common Needs Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What All Boards and Institutions Need</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {commonNeeds.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Works for All Boards and Institutions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔄</div>
            <h4 className="font-bold text-slate-900 text-sm">Flexible Configuration</h4>
            <p className="text-xs text-slate-600">Adapts to any board or university requirement</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📋</div>
            <h4 className="font-bold text-slate-900 text-sm">Multi-Format Reporting</h4>
            <p className="text-xs text-slate-600">Reports for CBSE, ICSE, State Board, or UGC</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🌐</div>
            <h4 className="font-bold text-slate-900 text-sm">Multi-Language Support</h4>
            <p className="text-xs text-slate-600">Works in multiple Indian languages</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Comprehensive Tracking</h4>
            <p className="text-xs text-slate-600">Attendance, academics, co-scholastic, or research</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔒</div>
            <h4 className="font-bold text-slate-900 text-sm">Secure Data Management</h4>
            <p className="text-xs text-slate-600">Protect all student and staff data</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Scalable Platform</h4>
            <p className="text-xs text-slate-600">Grow from one school to a university group</p>
          </div>
        </div>
      </div>

      {/* The Bottom Line */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Bottom Line</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Choosing an ERP that works across boards and institution types is essential for educational groups and trusts. You need a system that provides the right features for each board while maintaining consistency across your group.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>One platform. Multiple boards. All the features you need.</strong>
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">One ERP for All Boards and Institutions</h3>
        <p className="text-indigo-800 leading-relaxed">
          Different boards have different needs. CBSE, ICSE, State Boards, and Universities each require specific features and reporting formats. But that doesn't mean you need multiple systems.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon provides a single platform that adapts to any board or institution type. Flexible configuration, multi-format reporting, and comprehensive tracking—all in one system.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for all boards and universities.</strong> One platform. All the features you need.
        </p>
      </div>
    </>
  );
};

export default BoardComparison;